// src/pages/itsm/ITSMTeams.tsx
import { useState, useEffect } from 'react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Sun, Moon, Crown, X } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Btn, EmptyState, Avatar, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'



export default function ITSMTeams() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [teams, setTeams] = useState<any[]>([])
    const [agents, setAgents] = useState<any[]>([])
    const [showAdd, setShowAdd] = useState(false)
    const [editingTeam, setEditingTeam] = useState<any>(null)
    const [showMembers, setShowMembers] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [form, setForm] = useState({ name: '', description: '', color: '#2563eb', lead_agent_id: '' })
    const [confirmDeleteTeam, setConfirmDeleteTeam] = useState<{ id: number; name: string } | null>(null)

    useEffect(() => { fetchAll() }, [])

    const fetchAll = async () => {
        const [{ data: t }, { data: a }] = await Promise.all([
            supabase.from('itsm_teams').select(`
                *,
                itsm_agents!lead_agent_id(id, name),
                itsm_team_agents(agent_id, itsm_agents(id, name, role, email))
            `).order('name'),
            supabase.from('itsm_agents').select('id, name, role').eq('is_active', true).order('name'),
        ])
        setTeams(t || [])
        setAgents(a || [])
        setLoading(false)
    }

    const saveTeam = async () => {
        if (editingTeam) {
            await supabase.from('itsm_teams').update({
                ...form,
                lead_agent_id: form.lead_agent_id || null,
            }).eq('id', editingTeam.id)
        } else {
            await supabase.from('itsm_teams').insert([{
                ...form,
                lead_agent_id: form.lead_agent_id || null,
            }])
        }
        setForm({ name: '', description: '', color: '#2563eb', lead_agent_id: '' })
        setShowAdd(false)
        setEditingTeam(null)
        fetchAll()
        showToast('Team saved', 'success')
    }

    const deleteTeam = async (id: number) => {
        await supabase.from('itsm_teams').delete().eq('id', id)
        fetchAll()
        showToast('Team deleted', 'success')
    }

    const addMember = async (teamId: number, agentId: string) => {
        if (!agentId) return
        await supabase.from('itsm_team_agents').insert([{ team_id: teamId, agent_id: Number(agentId) }])
        fetchAll()
        const updated = teams.find(t => t.id === teamId)
        if (updated) setShowMembers(updated)
    }

    const removeMember = async (teamId: number, agentId: number) => {
        await supabase.from('itsm_team_agents').delete()
            .eq('team_id', teamId).eq('agent_id', agentId)
        fetchAll()
    }

    const ROLE_TONE: Record<string, 'info' | 'violet' | 'warning' | 'success' | 'danger'> = {
        agent: 'info', senior_agent: 'violet',
        team_lead: 'warning', manager: 'success', admin: 'danger'
    }
    const roleColor = (role?: string) => T[ROLE_TONE[role || ''] || 'info']

    if (loading) return <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Teams</h1>
                    <p style={s.subtitle}>{teams.length} teams · {agents.length} active agents</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ New Team</Btn>
                </div>
            </div>

            <div style={s.grid}>
                {teams.map(team => {
                    const members = team.itsm_team_agents || []
                    return (
                        <Card key={team.id} style={{ borderTop: `3px solid ${team.color}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <div>
                                    <p style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 2 }}>{team.name}</p>
                                    {team.description && <p style={{ fontSize: 12, color: T.text2 }}>{team.description}</p>}
                                    {team.itsm_agents && (
                                        <p style={{ fontSize: 11, color: T.text3, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Crown size={11} /> Lead: {team.itsm_agents.name}
                                        </p>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Btn size="sm" style={undefined} onClick={() => {
                                        setEditingTeam(team)
                                        setForm({ name: team.name, description: team.description || '', color: team.color, lead_agent_id: team.lead_agent_id || '' })
                                        setShowAdd(true)
                                    }}>Edit</Btn>
                                    <Btn size="sm" style={{ color: T.danger }} onClick={() => setConfirmDeleteTeam({ id: team.id, name: team.name })}>Delete</Btn>
                                </div>
                            </div>

                            {/* Members */}
                            <div style={{ borderTop: `0.5px solid ${T.border}`, paddingTop: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <p className="jr-eyebrow">Members ({members.length})</p>
                                    <Btn size="sm" style={{ color: T.accent }} onClick={() => setShowMembers(team)}>
                                        Manage
                                    </Btn>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {members.slice(0, 4).map((m: any) => (
                                        <Avatar key={m.agent_id} name={m.itsm_agents?.name} size={28} color={roleColor(m.itsm_agents?.role)} />
                                    ))}
                                    {members.length > 4 && (
                                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: T.text3 }}>
                                            +{members.length - 4}
                                        </div>
                                    )}
                                    {members.length === 0 && <p style={{ fontSize: 12, color: T.text3 }}>No members yet</p>}
                                </div>
                            </div>
                        </Card>
                    )
                })}
                {teams.length === 0 && (
                    <div style={{ gridColumn: 'span 3' }}>
                        <EmptyState title="No teams yet" sub='Click "+ New Team" to create one' />
                    </div>
                )}
            </div>

            {/* Add/Edit Team Modal */}
            {showAdd && (
                <div style={s.overlay}>
                    <div style={s.modal}>
                        <h2 style={s.modalTitle}>{editingTeam ? 'Edit Team' : 'New Team'}</h2>
                        <div style={s.field}>
                            <label style={s.label}>Team Name *</label>
                            <input style={s.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="IT Support Team" />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Description</label>
                            <input style={s.input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={s.field}>
                                <label style={s.label}>Team Color</label>
                                <input type="color" style={{ ...s.input, padding: 4, height: 36 }} value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Team Lead</label>
                                <select style={s.input} value={form.lead_agent_id} onChange={e => setForm({ ...form, lead_agent_id: e.target.value })}>
                                    <option value="">No Lead</option>
                                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                            <Btn style={s.btnOutline} onClick={() => { setShowAdd(false); setEditingTeam(null) }}>Cancel</Btn>
                            <Btn variant="primary" style={undefined} onClick={saveTeam}>Save</Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Members Modal */}
            {showMembers && (
                <div style={s.overlay}>
                    <div style={{ ...s.modal, maxWidth: 520 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h2 style={s.modalTitle}>{showMembers.name} — Members</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text3, display: 'flex' }} onClick={() => setShowMembers(null)}><X size={18} /></button>
                        </div>

                        {/* Add Member */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            <select id="addMemberSelect" style={{ ...s.input, flex: 1 }}>
                                <option value="">Select agent to add...</option>
                                {agents.filter(a => !showMembers.itsm_team_agents?.find((m: any) => m.agent_id === a.id))
                                    .map((a: any) => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
                            </select>
                            <Btn variant="primary" style={undefined} onClick={() => {
                                const sel = document.getElementById('addMemberSelect') as HTMLSelectElement
                                addMember(showMembers.id, sel.value)
                                sel.value = ''
                            }}>Add</Btn>
                        </div>

                        {/* Current Members */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {(teams.find(t => t.id === showMembers.id)?.itsm_team_agents || []).map((m: any) => (
                                <div key={m.agent_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: T.surface2, borderRadius: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <Avatar name={m.itsm_agents?.name} size={32} color={roleColor(m.itsm_agents?.role)} />
                                        <div>
                                            <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{m.itsm_agents?.name}</p>
                                            <p style={{ fontSize: 11, color: T.text3 }}>{m.itsm_agents?.role?.replace('_', ' ')}</p>
                                        </div>
                                    </div>
                                    <Btn size="sm" style={{ background: T.dangerBg, color: T.danger, borderColor: T.dangerBorder }}
                                        onClick={() => removeMember(showMembers.id, m.agent_id)}>
                                        Remove
                                    </Btn>
                                </div>
                            ))}
                            {(teams.find(t => t.id === showMembers.id)?.itsm_team_agents || []).length === 0 && (
                                <p style={{ textAlign: 'center', color: T.text3, fontSize: 13, padding: '20px' }}>No members yet</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!confirmDeleteTeam}
                onClose={() => setConfirmDeleteTeam(null)}
                onConfirm={() => confirmDeleteTeam && deleteTeam(confirmDeleteTeam.id)}
                title="Delete team?"
                message={`This will permanently delete "${confirmDeleteTeam?.name}". This cannot be undone.`}
                confirmLabel="Delete"
                danger
            />
            <ToastHost />
        </div>
    )
}

function ThemeToggle({ theme, toggleTheme }: { theme: 'light' | 'dark'; toggleTheme: () => void }) {
    return (
        <button onClick={toggleTheme} aria-label="Toggle theme" style={{
            width: 34, height: 34, borderRadius: T.radiusSm, background: T.surface,
            border: `1px solid ${T.borderStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: T.text2,
        }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
    )
}

const s: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
    btnOutline: { flex: 1, background: 'transparent' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%', maxWidth: 480, border: `1px solid ${T.border}` },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
